import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select, message } from "antd";
import ReportJewelryReportService from "../../../services/reports-Jewelry-service";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { RETAILER_CLICKS_CONFIG } from "./_shared/jewelryReportConfigs";
import JewelryDetailSearchTable from "./_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "./_shared/jewelryHelpers";

const { Option } = Select;

// This report groups clicks BY retailer, so the only cross-filter that makes
// sense is "which vendor(s)' traffic" -- no Retailer(s) select here, mirroring
// the pre-redesign page (ported as-is, not a new decision). Also ported as-is:
// the chart fetch never sends retailerIds (pre-existing behaviour, only
// vendorIds narrows the "Clicks by Retailer" breakdown).
const JewelryRetailerClickReportMain = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMembershipType, setDealerMembershipType] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerClicksData, setRetailerClicksData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleCheckDealermembers(id);
    }
    handleGetVendorDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckDealermembers = (id) => {
    ReportJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const memberType = loginDetials.loginDetials.responseData.dealerMemberTypeName;
        setDealerMembershipType(memberType);
        const fd = memberType === "Vendor" || memberType === "VendorDiamond" || memberType === "VendorGemstone" ? 1 : memberType === "Retailer" || memberType === "Combined" ? 2 : 3;
        setForDealer(fd);
        handleLoadRetailerClicksData(id, fd, dataRangeFrom, dataRangeTo, []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
  };

  const handleLoadRetailerClicksData = (dealerIdArg, forDealerArg, fromDate, toDate, vIds) => {
    setLoading(true);
    AdminJewelryReportService.GetClicksBasedOnRetailer({
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: "",
      vendorIds: extractDealerIds(vIds).toString(),
      reporttype: "Dealer",
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const rows = response.data.responseData;
          setRetailerClicksData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.total || 0), 0));
        } else {
          setRetailerClicksData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setRetailerClicksData([]);
        setTotalClicks(0);
      });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      AdminJewelryReportService.GetClicksBasedOnRetailer,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg,
        forDealer: forDealerArg,
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: "",
        vendorIds: extractDealerIds(vIds).toString(),
        reporttype: "Dealer",
      }),
      fromDate,
      toDate,
      "total",
      (data) => data
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterValue("");
    handleLoadRetailerClicksData(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = (retailerName) => {
    setFilterValue(retailerName);
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(RETAILER_CLICKS_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [`Vendors: ${vendorNames}`, `Total Clicks: ${totalClicks}`],
        fileName: "Jewelry-Retailer-Clicks-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId ? dealerId.toString() : "",
      forDealer: forDealer ? forDealer.toString() : "",
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: "",
      vendorIds: extractDealerIds(vendorIds).toString(),
      reporttype: "Dealer",
      reportName: "RetailerClick",
      barViewDimensions: chartType === "bar" ? "RetailerClick" : "",
    };
    setActivityPdfLoading(true);
    ReportJewelryReportService.ExportMetricPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const filename = `Jewelry_RetailerClick_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, filename, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  const sortedRetailers = [...retailerClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
  const topRetailer = sortedRetailers[0];
  const top3Sum = sortedRetailers.slice(0, 3).reduce((sum, row) => sum + Number(row.total || 0), 0);
  const top3Pct = totalClicks > 0 ? Math.round((top3Sum / totalClicks) * 100) : 0;
  const topPct = totalClicks > 0 && topRetailer ? ((Number(topRetailer.total) / totalClicks) * 100).toFixed(2) : 0;

  return (
    <ReportScaffold
      config={RETAILER_CLICKS_CONFIG}
      report={{
        [RETAILER_CLICKS_CONFIG.dataKey]: retailerClicksData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onDownloadActivityPdf={handleDownloadActivityPdf}
      downloadingActivityPdf={activityPdfLoading}
      onChartClick={handleSliceClick}
      onChartTypeChange={setChartType}
      insight={
        topRetailer && (
          <div className="edr-insight">
            💡 <b>{topRetailer.dealerCompany}</b> {t("is the standout retailer at")} <b>{topPct}%</b> {t("of clicks")}, {t("and the top 3 retailers capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("engagement is highly concentrated in a few retailers.")
              : t("engagement is reasonably spread across retailers.")}
          </div>
        )
      }
      extraFilterFields={
        /* Bug 1849: Vendor(s) filter removed from regular Jewelry Reports
        <div className="col-md-3">
          <div className="input__block">
            <label>{t("Vendor(s)")}</label>
            <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
              {vendorList.map((item) => (
                <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
              ))}
            </Select>
          </div>
        </div>
        */
        <></>
      }
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={[]}
          dealerMembershipType={dealerMembershipType}
          // AdminJewelryTopVendorRetailerChartColunm joins on VendorID (not
          // RetailerID) for @ForDealer=2 -- so for a Retailer-type account
          // this chart's "DealerCompany" rows are actually vendor names.
          // Drilling into the table has to filter on `vendor`, not
          // `retailer`, or the clicked name never matches anything (Bug 1777).
          filterParamName={forDealer === 2 ? "vendor" : RETAILER_CLICKS_CONFIG.detail.filterParamName}
          filterValue={filterValue}
          fileNamePart={RETAILER_CLICKS_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default JewelryRetailerClickReportMain;
